import {
  useCallback,
  useEffect,
  useState,
  type FC,
  type ReactNode,
} from 'react';
import { flushSync } from 'react-dom';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  DataTableSkeleton,
  InlineNotification,
  SkeletonText,
  Tag,
} from '@carbon/react';
import { Checkmark, Rocket, TaskView, TrashCan } from '@carbon/react/icons';
import {
  ActivityFeedPanel,
  ActivityFeedPanelOpenButton,
} from 'components/activity-feed-panel';
import {
  AgentSurface,
  AgentTriggerButton,
  useAgentDockWidth,
  useAgentPanel,
} from 'components/agent-chat/agent-surface';
import { TITLE_SUPPORT_WORKSPACE_AGENT } from 'components/agent-chat/agents/title-support-workspace-agent';
import { DestroyConfirmation } from 'components/destroy-confirmation';
import { EmptyState } from 'components/empty-state';
import { DualSidebarLayout } from 'components/layout/dual-sidebar-layout';
import { SuccessToast } from 'components/success-toast';
import {
  VerificationTasksPanel,
  type VerificationTask,
} from 'components/verification-tasks-panel';
import {
  CardSlot,
  isCardVisible,
  useDockOffsets,
} from 'components/workspace-cards';
import { WorkspaceShellHeader } from 'components/workspace-shell-header';
import { PromoteModal } from 'pages/pre-drill-wellbore-workspace/promote-modal';
import {
  readWriteFailure,
  UNEXPLAINED_FAILURE,
} from 'pages/well-workspaces/utils';
import { subscribe } from 'lib/pusher';
import { useEscapeToClose } from 'lib/hooks/useEscapeToClose';
import routes from '~/routes';
import {
  agentMessagesHref,
  deleteTract,
  deleteWorkspace,
  keys,
  postPromotion,
  useRefreshWorkspace,
  useWorkspace,
  useWorkspaceWrite,
  workspacePath,
} from '../api';
import type { TaskState, WorkspaceTract } from '../types';
import { OpenDraftsWarning } from '../pickers';
import {
  CARDS,
  confirmProps,
  type CardId,
  type CardVisibility,
  type JumpToCard,
} from './columns';
import {
  LOCKED_EXPLANATION,
  OwnershipComparison,
  RunsheetComparison,
} from './comparisons';
import {
  StandardMorCard,
  TitleSupportCard,
  TractsCard,
  isConverting,
} from './overview';
import {
  EMPTY_FILTERS,
  type FilterTable,
  type ReportRows,
  type TableRows,
  type WorkspaceFilters,
} from './filters';
import { Rail } from './rail';
import styles from './page.module.scss';

const PUSHER_EVENTS = [
  'workbook-attached',
  'conversion-started',
  'conversion-completed',
  'conversion-failed',
];
const TASK_STATE_TAGS: Record<
  TaskState,
  { label: string; type: 'green' | 'gray' }
> = {
  done: { label: 'Done', type: 'green' },
  pending: { label: 'Pending', type: 'gray' },
  locked: { label: 'Locked', type: 'gray' },
};

const TaskTag = ({ status }: { status: TaskState }) => (
  <Tag type={TASK_STATE_TAGS[status].type} size="sm">
    {TASK_STATE_TAGS[status].label}
  </Tag>
);

type Drawer = 'tasks' | 'link' | null;

export const TitleSupportWorkspacePage: FC = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  useEffect(() => {
    const shell = document.getElementById('main-content');
    shell?.classList.add('title-support-workspace-fullbleed');
    return () => shell?.classList.remove('title-support-workspace-fullbleed');
  }, []);
  const { state } = useLocation<{ created?: boolean } | undefined>();
  const queryClient = useQueryClient();
  const [createdToast, setCreatedToast] = useState(state?.created === true);
  const [promotedToast, setPromotedToast] = useState(false);
  const [promoteOpen, setPromoteOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  // One right-hand drawer at a time: Tasks, Link Item or the agent.
  const [drawer, setDrawerState] = useState<Drawer>(null);
  const [agentOpen, setAgentOpen] = useAgentPanel(
    TITLE_SUPPORT_WORKSPACE_AGENT
  );
  const setDrawer = useCallback(
    (next: Drawer) => {
      setDrawerState(next);
      setAgentOpen(false);
    },
    [setAgentOpen]
  );
  useEffect(() => {
    if (agentOpen) setDrawerState(null);
  }, [agentOpen]);
  const closeTasks = useCallback(() => setDrawerState(null), []);
  const tasksOpen = drawer === 'tasks';
  // Link Item's panel handles its own Escape.
  useEscapeToClose(tasksOpen, closeTasks);
  const onLinkOpenChange = useCallback(
    (open: boolean) => setDrawer(open ? 'link' : null),
    [setDrawer]
  );
  const [visible, setVisible] = useState<CardVisibility>({});
  const [filters, setFilters] = useState<WorkspaceFilters>(EMPTY_FILTERS);
  const [tableRows, setTableRows] = useState<
    Partial<Record<FilterTable, TableRows>>
  >({});
  const reportRows = useCallback<ReportRows>(
    (table, rows) => setTableRows((current) => ({ ...current, [table]: rows })),
    []
  );
  const [removeTarget, setRemoveTarget] = useState<WorkspaceTract | null>(null);
  const remove = useWorkspaceWrite(
    (salesforceTractId: string) => deleteTract(id, salesforceTractId),
    () => setRemoveTarget(null)
  );
  const { reset: resetRemove } = remove;
  const openRemoveTract = useCallback(
    (tract: WorkspaceTract) => {
      resetRemove();
      setRemoveTarget(tract);
    },
    [resetRemove]
  );
  const [pinned, setPinned] = useState<Set<string>>(new Set());
  const togglePin = useCallback(
    (card: string) =>
      setPinned((current) => {
        const next = new Set(current);
        if (!next.delete(card)) next.add(card);
        return next;
      }),
    []
  );
  const offsets = useDockOffsets(pinned, JSON.stringify(visible));
  const jumpTo = useCallback<JumpToCard>((card) => {
    flushSync(() => setVisible((current) => ({ ...current, [card]: true })));
    document
      .querySelector(`[data-section="${card}"]`)
      ?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }, []);
  const dockWidth = useAgentDockWidth(TITLE_SUPPORT_WORKSPACE_AGENT);

  const detail = useWorkspace(id, {
    refetchInterval: (query) => {
      if (query.state.data?.titleSupport.supports.some(isConverting)) {
        return 10_000;
      }
      return false;
    },
  });

  const refresh = useRefreshWorkspace();
  useEffect(() => {
    const channel = subscribe(`title_support_workspace_${id}`);
    PUSHER_EVENTS.forEach((event) => channel.bind(event, refresh));
    return () => channel.unsubscribe();
  }, [id, refresh]);

  const promotion = useMutation({
    mutationFn: () => postPromotion(id),
    retry: false,
    onSuccess: (workspace) => {
      queryClient.setQueryData(keys.detail(id), workspace);
      queryClient.invalidateQueries({ queryKey: keys.list() });
      queryClient.invalidateQueries({ queryKey: keys.summary() });
      setPromoteOpen(false);
      setPromotedToast(true);
    },
  });

  const removal = useMutation({
    mutationFn: () => deleteWorkspace(id),
    retry: false,
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: keys.detail(id) });
      queryClient.invalidateQueries({ queryKey: keys.list() });
      queryClient.invalidateQueries({ queryKey: keys.summary() });
      history.push(routes.titleSupportWorkspacesPath);
    },
  });

  const data = detail.data;
  const tasks = data?.titleSupport.tasks ?? [];
  const completedTasks = tasks.filter((task) => task.status === 'done').length;
  const statusOf = (path: string | number) =>
    tasks.find((task) => task.path === path)?.status;
  const panelTasks: VerificationTask[] = tasks.map((task) => ({
    id: task.path,
    label: task.title,
    description: task.description || undefined,
    status: task.status === 'done' ? true : null,
    eventsHref: '',
    disabled: task.status === 'locked',
    lockReason: task.status === 'locked' ? LOCKED_EXPLANATION : null,
  }));

  if (detail.isPending)
    return (
      <div className={styles.page}>
        <WorkspaceShellHeader title={<SkeletonText heading width="240px" />} />
        <main className={styles.main}>
          <DataTableSkeleton columnCount={8} rowCount={8} showHeader={false} />
        </main>
      </div>
    );
  if (detail.isError || !data) {
    return (
      <EmptyState
        headerText={
          detail.isError ? 'Failed to load workspace' : 'Workspace not found'
        }
      />
    );
  }

  const cards: Record<CardId, ReactNode> = {
    'tracts': (
      <TractsCard
        id={id}
        detail={data}
        onRemoveTract={openRemoveTract}
        filters={filters.tracts}
        onRows={reportRows}
      />
    ),
    'title-support': (
      <TitleSupportCard
        id={id}
        detail={data}
        linkOpen={drawer === 'link'}
        onLinkOpenChange={onLinkOpenChange}
      />
    ),
    'standard-mor': (
      <StandardMorCard id={id} filters={filters.mor} onRows={reportRows} />
    ),
    'runsheet-comparison': (
      <RunsheetComparison
        id={id}
        detail={data}
        locked={statusOf('compare_runsheet') === 'locked'}
        filters={filters.runsheet}
        onRows={reportRows}
      />
    ),
    'ownership-comparison': (
      <OwnershipComparison
        id={id}
        detail={data}
        locked={statusOf('compare_ownership') === 'locked'}
        onJump={jumpTo}
        filters={filters.ownership}
        onRows={reportRows}
      />
    ),
  };

  return (
    <div className={styles.page} style={{ paddingRight: dockWidth }}>
      <WorkspaceShellHeader
        breadcrumbs={[
          [routes.titleSupportWorkspacesPath, 'Title Support Workspaces'],
          [workspacePath(id), data.name],
        ]}
        onBack={() => {
          if (history.length > 1) history.goBack();
          else history.push(routes.titleSupportWorkspacesPath);
        }}
        backLabel="Back"
        title={data.name}
        subtitle={`${data.titleSupport.tracts.length} ${
          data.titleSupport.tracts.length === 1 ? 'tract' : 'tracts'
        } · ${
          data.promoted ? `Promoted by ${data.promotedBy ?? 'System'}` : 'Draft'
        }`}
        actions={
          <>
            <AgentTriggerButton
              agent={TITLE_SUPPORT_WORKSPACE_AGENT}
              label="Converse with the agent"
              size="md"
            />
            <ActivityFeedPanelOpenButton tooltipPosition="bottom" />
            <Button
              kind="ghost"
              size="md"
              renderIcon={TaskView}
              aria-pressed={tasksOpen}
              onClick={() => setDrawer(tasksOpen ? null : 'tasks')}
            >
              Tasks {completedTasks}/{tasks.length}
            </Button>
            {!data.promoted && (
              <Button
                kind="danger--ghost"
                size="md"
                renderIcon={TrashCan}
                onClick={() => {
                  removal.reset();
                  setDeleteOpen(true);
                }}
              >
                Delete workspace
              </Button>
            )}
            <Button
              kind="primary"
              size="md"
              renderIcon={data.promoted ? Checkmark : Rocket}
              disabled={!data.titleSupport.promotable}
              title={
                data.promoted || data.titleSupport.promotable
                  ? undefined
                  : 'Complete every task first'
              }
              onClick={() => {
                promotion.reset();
                setPromoteOpen(true);
              }}
            >
              {data.promoted ? 'Promoted' : 'Promote'}
            </Button>
          </>
        }
      />
      <DualSidebarLayout
        left={{
          isOpen: true,
          width: 324,
          content: (
            <Rail
              visible={visible}
              onChange={setVisible}
              filters={filters}
              rows={tableRows}
              onFiltersChange={(table, next) =>
                setFilters((current) => ({ ...current, [table]: next }))
              }
            />
          ),
        }}
        right={{
          isOpen: tasksOpen,
          width: 364,
          content: tasksOpen ? (
            <VerificationTasksPanel
              tasks={panelTasks}
              readOnly
              title="Tasks"
              groupLabel="Title Support"
              emptyMessage="No tasks."
              onClose={closeTasks}
              renderStatusTag={(task) => (
                <TaskTag status={statusOf(task.id) ?? 'pending'} />
              )}
            />
          ) : null,
        }}
      >
        <main className={styles.main}>
          {!data.promoted && (
            <OpenDraftsWarning
              tracts={data.titleSupport.tracts.map((tract) => ({
                id: tract.salesforceTractId,
                name: tract.name,
              }))}
              workspaceId={id}
            />
          )}
          {CARDS.every((card) => !isCardVisible(visible, card.id)) && (
            <EmptyState
              headerText="Every card is hidden"
              helperText="Turn cards back on in Layout Settings."
            />
          )}
          {CARDS.map((card) => (
            <CardSlot
              key={card.id}
              sectionId={card.id}
              pinnedIds={pinned}
              onTogglePin={togglePin}
              stickyTop={offsets[card.id] ?? 0}
              hidden={!isCardVisible(visible, card.id)}
            >
              {cards[card.id]}
            </CardSlot>
          ))}
        </main>
      </DualSidebarLayout>

      <DestroyConfirmation
        open={removeTarget !== null}
        modalHeading={`Remove ${
          removeTarget?.name ?? 'tract'
        } from this workspace`}
        size="sm"
        primaryButtonText="Yes, remove"
        {...confirmProps(remove, 'Could not remove the tract.', () =>
          setRemoveTarget(null)
        )}
        onRequestSubmit={() =>
          removeTarget && remove.mutate(removeTarget.salesforceTractId)
        }
      >
        The tract keeps its vesting documents and workbooks; only its place in
        this workspace goes.
      </DestroyConfirmation>
      <DestroyConfirmation
        open={deleteOpen}
        modalHeading={`Delete ${data.name}`}
        size="sm"
        {...confirmProps(removal, 'Could not delete the workspace.', () =>
          setDeleteOpen(false)
        )}
        onRequestSubmit={() => removal.mutate()}
      >
        The workspace and its task state go; its tracts, vesting documents and
        workbooks stay.
      </DestroyConfirmation>
      <PromoteModal
        open={promoteOpen}
        workspaceName={data.name}
        loading={promotion.isPending}
        error={false}
        onClose={() => setPromoteOpen(false)}
        onConfirm={() => promotion.mutate()}
      >
        <p className={styles.modalDescription}>
          Promoting freezes this workspace: its tracts, workbooks and links can
          no longer change. Any changes must be done in another workspace.
        </p>
        {promotion.error ? (
          <InlineNotification
            kind="error"
            lowContrast
            hideCloseButton
            title="Promote failed"
            subtitle={
              readWriteFailure(
                promotion.error,
                `Could not promote the workspace. ${UNEXPLAINED_FAILURE}`
              ) ?? undefined
            }
          />
        ) : null}
        <ul className={styles.taskList}>
          {tasks.map((task) => (
            <li key={task.path} className={styles.taskRow}>
              <span>{task.title}</span>
              <TaskTag status={task.status} />
            </li>
          ))}
        </ul>
      </PromoteModal>
      {createdToast && (
        <SuccessToast
          message="Workspace created"
          onClose={() => {
            setCreatedToast(false);
            return true;
          }}
        />
      )}
      {promotedToast && (
        <SuccessToast
          message="Workspace promoted"
          subtitle="The workspace is now frozen."
          onClose={() => {
            setPromotedToast(false);
            return true;
          }}
        />
      )}
      <ActivityFeedPanel
        modelType="TitleSupportWorkspace"
        modelId={data.id}
        title="Activity"
        subtitle={data.name}
        commentsHref={data.resources.comments.href}
      />
      <AgentSurface
        agent={TITLE_SUPPORT_WORKSPACE_AGENT}
        agentMessagesHref={agentMessagesHref(id)}
        subject={{ type: 'TitleSupportWorkspace', id: data.id }}
        subtitle={`Ask about ${data.name}`}
      />
    </div>
  );
};
